import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useShouldApplyPRODiscount from 'hooks/Growth/useShouldApplyPRODiscount';
import useUserPeppolIntegration from 'hooks/Peppol/useUserPeppolIntegration';
import { useSubscriptionPlan } from 'hooks/Subscriptions';
import _get from 'lodash/get';
import {
  useCallback,
  useReducer,
  type FC,
  type PropsWithChildren,
} from 'react';
import { IntegrationsStatusEnum } from 'types/integrations';
import { AvailableCountries, VATTypeEnum } from 'types/users.types';
import { isSubscriptionTrialPlan } from 'utils/subscriptions.utils';
import { AppBannersContext } from './context';
import { BANNERS_STORAGE_KEYS } from './helpers';

const banners = Object.keys(BANNERS_STORAGE_KEYS) as BANNERS_STORAGE_KEYS[];

const AppBannersProvider: FC<PropsWithChildren<any>> = ({ children }) => {
  const {
    GROW_DISCOUNT_BANNER_DISMISS_KEY,
    GERMAN_PRO_PLAN_DISCOUNT_BANNER_DISMISS_KEY,
    TRIAL_BANNER_DISMISS_KEY,
    BELGIUM_PEPPOL_VERIFICATION_DISMISS_KEY,
  } = BANNERS_STORAGE_KEYS;

  const { data: user } = useGetCurrentCustomerData();

  const VATType = user?.VATType;

  const isGermanUser = user?.country === AvailableCountries.germany;
  const isBelgiumUser = user?.country === AvailableCountries.belgium;

  const shouldApplyPRODiscount = useShouldApplyPRODiscount();

  const plan = useSubscriptionPlan();

  const shouldShowTrialBanner = isSubscriptionTrialPlan(plan?.planInternalName);

  const peppol = useUserPeppolIntegration();

  const [state, dispatch] = useReducer(
    (prev: Record<string, any>, next: Record<string, any>) => ({
      ...prev,
      ...next,
    }),
    banners.reduce((all, key) => {
      const keyName = _get(BANNERS_STORAGE_KEYS, [key]);
      return {
        ...all,
        [keyName]: !!localStorage.getItem(keyName),
      };
    }, {}),
  );

  const dismissBanner = useCallback((dismissKey: BANNERS_STORAGE_KEYS) => {
    dispatch({ [dismissKey]: true });
    localStorage.setItem(dismissKey, 'true');
  }, []);

  return (
    <AppBannersContext.Provider
      value={{
        displayGrowDiscountBanner:
          !state[GROW_DISCOUNT_BANNER_DISMISS_KEY] &&
          isSubscriptionTrialPlan(plan?.planInternalName) &&
          isGermanUser &&
          VATType === VATTypeEnum.franchisee,
        displayGermanProPlanDiscountBanner: !!(
          !state[GERMAN_PRO_PLAN_DISCOUNT_BANNER_DISMISS_KEY] &&
          isSubscriptionTrialPlan(plan?.planInternalName) &&
          shouldApplyPRODiscount &&
          VATType === VATTypeEnum.subjectToVAT
        ),
        displayTRIALBanner:
          shouldShowTrialBanner && !state[TRIAL_BANNER_DISMISS_KEY],
        displayBelgiumPeppolVerificationBanner:
          !state[BELGIUM_PEPPOL_VERIFICATION_DISMISS_KEY] &&
          isBelgiumUser &&
          !!peppol.data &&
          !peppol.data.requiresUserVerification &&
          peppol.data.status === IntegrationsStatusEnum.active,
        dismissBanner,
      }}
    >
      {children}
    </AppBannersContext.Provider>
  );
};

export default AppBannersProvider;
